<template>
  <div class="vendor-accounts">
    <h3 :style="{ fontWeight: 'bold', color: '#195BFF', marginBottom: '20px' }">
      <i class="fal fa-store"></i>
      ຈັດການ Vendor (Third-party Integration)
    </h3>

    <div class="vendor-grid">
      <!-- ================= Create new vendor ================= -->
      <div class="panel">
        <h4 class="panel-title">
          <i class="fal fa-plus-circle"></i> ສ້າງ Vendor ໃໝ່
        </h4>

        <div class="field">
          <label class="label">ຊື່ <span class="text-danger">* {{ createErrors.name }}</span></label>
          <input class="input" type="text" v-model="createForm.name"
                 placeholder="ຊື່ບໍລິສັດ / vendor" />
        </div>

        <div class="field">
          <label class="label">ອີເມວ <span class="text-danger">* {{ createErrors.email }}</span></label>
          <input class="input" type="text" v-model="createForm.email" @input="emailAuto = false"
                 placeholder="slug@client.hal-logistics.la" />
        </div>

        <div class="field">
          <label class="label">ລະຫັດຜ່ານ <span class="text-danger">* {{ createErrors.password }}</span></label>
          <div class="pw-row">
            <input class="input" type="text" v-model="createForm.password" @input="passwordAuto = false"
                   placeholder="ຢ່າງໜ້ອຍ 8 ຕົວອັກສອນ" />
            <vs-button size="small" flat @click="regeneratePassword()" title="ສ້າງລະຫັດແນະນຳໃໝ່">
              <i class="fal fa-sync"></i>
            </vs-button>
          </div>
        </div>

        <div class="field">
          <label class="label">ເບີໂທ <span class="text-danger">* {{ createErrors.tel }}</span></label>
          <input class="input" type="text" v-model="createForm.tel"
                 placeholder="ເບີໂທຕິດຕໍ່" />
        </div>

        <div class="field">
          <label class="label">ຊື່ Client (API) <span class="text-danger">{{ createErrors.client_name }}</span></label>
          <input class="input" type="text" v-model="createForm.client_name"
                 :placeholder="createForm.name || 'ຄ່າເລີ່ມຕົ້ນ = ຊື່ vendor'" />
        </div>

        <vs-button flat block :loading="creating" :disabled="creating || !createValid" @click="createVendor()">
          ສ້າງ Vendor + ອອກ Client
        </vs-button>
      </div>

      <!-- ================= Grant existing (by phone) ================= -->
      <div class="panel">
        <h4 class="panel-title">
          <i class="fal fa-search"></i> ໃຫ້ສິດ Vendor ຕາມເບີໂທ
        </h4>

        <div class="field">
          <label class="label">ຄົ້ນຫາດ້ວຍເບີໂທ</label>
          <div class="search-row">
            <input class="input" type="text" v-model="searchTel"
                   placeholder="ປ້ອນເບີໂທ..." @keyup.enter="lookup()" />
            <vs-button flat :loading="searching" @click="lookup()">ຄົ້ນຫາ</vs-button>
          </div>
        </div>

        <p v-if="searched && !candidates.length" class="empty">ບໍ່ພົບບັນຊີທີ່ກົງກັບເບີນີ້</p>

        <table v-if="candidates.length" class="cand-table">
          <thead>
            <tr>
              <th>ຊື່</th>
              <th>ອີເມວ</th>
              <th>ເບີ</th>
              <th>Vendor</th>
              <th>Clients</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in candidates" :key="c.user_id">
              <td>{{ c.name }}</td>
              <td>{{ c.email }}</td>
              <td>{{ c.user_tel }}</td>
              <td>
                <span :class="c.is_vendor ? 'badge badge-on' : 'badge badge-off'">
                  {{ c.is_vendor ? 'ແມ່ນ' : 'ບໍ່' }}
                </span>
              </td>
              <td>{{ c.owned_client_count }}</td>
              <td class="actions">
                <vs-button size="small" flat
                           :loading="grantingId === c.user_id"
                           :disabled="c.is_vendor || grantingId === c.user_id"
                           @click="grant(c)">
                  ໃຫ້ສິດ
                </vs-button>
                <vs-button size="small" flat color="success"
                           :loading="generatingId === c.user_id"
                           :disabled="!c.is_vendor || generatingId === c.user_id"
                           @click="generateClient(c)">
                  ອອກ Client
                </vs-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ================= Show-once secret dialog ================= -->
    <vs-dialog v-model="secretDialog">
      <template #header>
        <h4 class="not-margin">{{ secretResult.title }}</h4>
      </template>

      <div class="secret-body">
        <p class="warn">
          <i class="fal fa-exclamation-triangle"></i>
          ບັນທຶກ Secret ດຽວນີ້ — ຈະສະແດງພຽງຄັ້ງດຽວເທົ່ານັ້ນ.
        </p>

        <div class="token-block">
          <div class="token-head">
            <span class="k">OAuth token request (POST /oauth/token)</span>
            <vs-button size="small" flat @click="copyTokenRequest()">
              {{ copied ? '✓ ຄັດລອກແລ້ວ' : 'Copy JSON' }}
            </vs-button>
          </div>
          <pre class="token-json">{{ tokenRequestJson }}</pre>
        </div>

        <div v-if="secretResult.email" class="secret-row">
          <span class="k">ອີເມວ</span>
          <span class="v">{{ secretResult.email }}</span>
          <vs-button size="small" flat @click="copyText(secretResult.email)">Copy</vs-button>
        </div>
        <div v-if="secretResult.password" class="secret-row">
          <span class="k">ລະຫັດຜ່ານ</span>
          <span class="v">{{ secretResult.password }}</span>
          <vs-button size="small" flat @click="copyText(secretResult.password)">Copy</vs-button>
        </div>
        <div class="secret-row">
          <span class="k">Client ID</span>
          <span class="v">{{ secretResult.client_id }}</span>
          <vs-button size="small" flat @click="copyText(secretResult.client_id)">Copy</vs-button>
        </div>
        <div class="secret-row">
          <span class="k">Client Secret</span>
          <span class="v">{{ secretResult.client_secret }}</span>
          <vs-button size="small" flat @click="copyText(secretResult.client_secret)">Copy</vs-button>
        </div>
      </div>

      <template #footer>
        <vs-button flat @click="secretDialog = false">ປິດ</vs-button>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import MixinVendorAccounts from '../../../Mixins/Vendor/VendorAccounts';

export default {
  name: 'VendorAccounts',
  mixins: [MixinVendorAccounts],
};
</script>

<style lang="scss" scoped>
.vendor-accounts {
  padding: 10px;
}

.vendor-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
}

.panel {
  flex: 1 1 380px;
  background: #fff;
  border: 1px solid rgba(var(--vs-gray-2), 1);
  border-radius: 8px;
  padding: 18px;
  text-align: left;
}

.panel-title {
  font-weight: bold;
  color: #195BFF;
  margin-bottom: 15px;
}

.search-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.pw-row {
  display: flex;
  gap: 8px;
  align-items: center;

  .input { flex: 1; }
}

.empty {
  color: #888;
  margin-top: 10px;
}

.cand-table {
  width: 100%;
  margin-top: 12px;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    border-bottom: 1px solid #eee;
    padding: 8px 6px;
    text-align: left;
  }

  .actions {
    display: flex;
    gap: 6px;
  }
}

.badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  color: #fff;

  &.badge-on { background: #28c76f; }
  &.badge-off { background: #b8b8b8; }
}

.secret-body {
  text-align: left;

  .warn {
    color: #ea5455;
    margin-bottom: 12px;
  }

  .token-block {
    margin-bottom: 16px;

    .token-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6px;

      .k { font-weight: bold; }
    }

    .token-json {
      margin: 0;
      background: #1e1e2f;
      color: #e0e0e0;
      padding: 12px;
      border-radius: 6px;
      font-family: monospace;
      font-size: 13px;
      line-height: 1.5;
      white-space: pre;
      overflow-x: auto;
    }
  }

  .secret-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;

    .k { width: 110px; font-weight: bold; }
    .v {
      flex: 1;
      word-break: break-all;
      font-family: monospace;
      background: #f5f5f5;
      padding: 4px 8px;
      border-radius: 4px;
    }
  }
}
</style>
